import { getPvtApiSampleId, getPvtTestListEndpoint } from "./pvtTestRoute";

const EXP_TO_PARAM_KEY = {
  cce: "cce",
  dv: "dv",
  dl: "dv",
  viscosity: "viscosity",
  visc: "viscosity",
};

const BUILDER_LABELS = {
  temp_scale: "Temperature Scale",
  comp_block_scale: "Component Block Scale",
  n_components: "PCA Components",
  n_neighbors: "UMAP Neighbors",
  custom_scheme: "Custom Lump Scheme",
  include_custom_distance_metric: "Include Custom Distance Metric",
  lumped_derivatives: "Lumped Derivatives",
};

const OTHER_FEATURE_LABELS = {
  Psat: "Psat Scale",
  "GOR (scf/stb)": "GOR Scale",
  "STO GOR (scf/stb)": "STO GOR Scale",
  "STO GOR": "STO GOR Scale",
  MW: "Molecular Weight Scale",
  "STO MW": "STO MW Scale",
  "Fluid Density (g/cc)": "Fluid Density Scale",
  "STO Density": "STO Density Scale",
};

const RUNNER_LABELS = {
  analysis_runner_name: "Analysis Runner Name",
  max_cluster_window: "Max Cluster Window",
  pred_threshold: "Prediction Threshold",
  max_neigh_return: "Max Neighbors Returned",
  top_k_opt: "Top K Optimization",
  weighting_mode: "Weighting Mode",
  distance_decay_rate: "Distance Decay Rate",
  outlier_threshold: "Outlier Threshold",
  max_relative_delta: "Max Relative Delta",
  lambda_reg: "Lambda Regularization",
  derivative_strategy_prediction: "Derivative Strategy (Prediction)",
  derivative_strategy_equations: "Derivative Strategy (Equations)",
  bo_derivative_scaling: "Bo Derivative Scaling",
  rs_derivative_scaling: "Rs Derivative Scaling",
  endpoint_estimator_type: "Endpoint Estimator Type",
};

const RUNNER_VALUE_LABELS = {
  analysis_hybrid: "Hybrid",
  analysis_hybrid_expanded_for_knn_comps: "Hybrid Expanded (KNN Components)",
  analysis_taylor_few_and_good_neighbors: "Taylor (Few and Good Neighbors)",
  cumulative: "Cumulative",
  individual: "Individual",
  both: "Both (Individual + Cumulative)",
  neighbor_only: "Neighbor Only",
  avg_neighbor_test: "Average Neighbor Test",
  Inverse: "Inverse",
  Equal: "Equal",
  Hybrid: "Hybrid",
  Taylor: "Taylor",
};

const HUMANIZE_ACRONYMS = new Set([
  "knn",
  "pca",
  "umap",
  "gor",
  "rs",
  "bo",
  "mw",
  "dl",
  "cce",
  "cp",
  "scf",
  "stb",
  "vr",
]);

const HUMANIZE_WORD_EXPANSIONS = {
  comps: "Components",
  neigh: "Neighbors",
  pred: "Prediction",
  opt: "Optimization",
};

const COMPONENT_MAPPING = {
  0: "N2",
  1: "CO2",
  2: "H2S",
  3: "C1",
  4: "C2",
  5: "C3",
  6: "iC4",
  7: "nC4",
  8: "iC5",
  9: "nC5",
  10: "C6",
  11: "C7",
  12: "C8",
  13: "C9",
  14: "C10",
  15: "C11",
  16: "C12",
  17: "C13",
  18: "C14",
  19: "C15",
  20: "C16",
  21: "C17",
  22: "C18",
  23: "C19",
  24: "C20",
  25: "C21",
  26: "C22",
  27: "C23",
  28: "C24",
  29: "C25",
  30: "C26",
  31: "C27",
  32: "C28",
  33: "C29",
  34: "C30",
  35: "C31",
  36: "C32",
  37: "C33",
  38: "C34",
  39: "C35",
  40: "C36+",
};

export function normalizePvtExperimentType(raw, fallback = "cce") {
  const et = String(raw || "").trim().toLowerCase();
  return EXP_TO_PARAM_KEY[et] || fallback;
}

function isSuccessStatus(response) {
  const statusCode = Number(response?.data?.statusCode);
  if (statusCode === 200 || statusCode === 201) {
    return true;
  }
  return response?.status === 200 || response?.status === 201;
}

function unwrapEntity(row) {
  if (!row || typeof row !== "object") {
    return {};
  }
  for (const key of ["PvtParams", "PvtSample"]) {
    const nested = row[key];
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return nested;
    }
  }
  const nestedData = row.data;
  if (
    nestedData &&
    typeof nestedData === "object" &&
    !Array.isArray(nestedData) &&
    (nestedData.mainConfig || nestedData.MainConfig || nestedData.PvtParams)
  ) {
    return unwrapEntity(nestedData);
  }
  return row;
}

function unwrapPayload(response) {
  const body = response?.data;
  if (!body || typeof body !== "object") {
    return null;
  }
  if (body.data !== undefined) {
    return body.data;
  }
  return body;
}

function asList(data) {
  if (data == null) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (typeof data === "object") {
    for (const key of ["items", "results", "params", "PvtParams", "data"]) {
      if (Array.isArray(data[key])) {
        return data[key];
      }
    }
    return [data];
  }
  return [];
}

function entityId(row) {
  if (!row || typeof row !== "object") {
    return asId(row);
  }
  return asId(row._id) || asId(row.id) || asId(row.ID);
}

function paramFileName(row) {
  return String(row?.name || row?.paramFileName || "").trim();
}

function asId(value, seen = new Set()) {
  if (value == null || value === "") {
    return "";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || trimmed === "[object Object]") {
      return "";
    }
    return trimmed;
  }
  if (typeof value !== "object" || seen.has(value)) {
    return "";
  }
  seen.add(value);
  if (Array.isArray(value)) {
    return asId(value[0], seen);
  }
  return (
    asId(value.$oid, seen) ||
    asId(value.oid, seen) ||
    asId(value.paramFileId, seen) ||
    asId(value.param_file_id, seen) ||
    asId(value._id, seen) ||
    asId(value.id, seen) ||
    asId(value.ID, seen)
  );
}

function paramBlockRaw(params, expType) {
  if (!params || typeof params !== "object") {
    return undefined;
  }
  const key = normalizePvtExperimentType(expType);
  const aliases =
    key === "dv"
      ? ["dv", "dl"]
      : key === "viscosity"
        ? ["viscosity", "visc"]
        : ["cce"];
  const entries = Object.entries(params);
  for (const alias of aliases) {
    const match = entries.find(([name]) => String(name).toLowerCase() === alias);
    if (match) {
      return match[1];
    }
  }
  return undefined;
}

function paramBlockForExperiment(sample, expType) {
  const raw = paramBlockRaw(sample?.predictionParams, expType);
  if (typeof raw === "string" && asId(raw)) {
    return { paramFileId: asId(raw) };
  }
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    return raw;
  }
  return {};
}

function paramIdFromBlock(block) {
  if (!block || typeof block !== "object") {
    return asId(block);
  }
  return (
    asId(block.paramFileId) ||
    asId(block.param_file_id) ||
    asId(block.paramFile)
  );
}

export function paramRefForExperiment(sample, expType) {
  const sources = [
    sample,
    sample?.sampleMeta,
    sample?.pvtSample,
    sample?.PvtSample,
  ].filter((item) => item && typeof item === "object");
  const expKey = normalizePvtExperimentType(expType);
  const testAliases = {
    cce: ["ccetest", "ccetests"],
    dv: ["dltest", "dltests", "dl"],
    viscosity: ["viscositytest", "viscositytests", "visc"],
  }[expKey] || [];
  const listedTests = Array.isArray(sample?.allTests) ? sample.allTests : [];
  const matchedTest = listedTests.find((test) => {
    const type = String(test?.testType || test?.TestType || test?.test_type || "").toLowerCase();
    return testAliases.includes(type);
  });
  if (matchedTest) {
    sources.push(matchedTest);
  }
  let name = "";
  let paramFileId = "";
  let modelId = "";
  let modelName = "";
  sources.forEach((source) => {
    const block = paramBlockForExperiment(source, expType);
    if (!name) {
      name = String(block.paramFileName || block.name || source.paramFileName || "").trim();
    }
    if (!paramFileId) {
      paramFileId =
        paramIdFromBlock(block) ||
        asId(source.paramFileId) ||
        asId(source.param_file_id);
    }
    if (!modelId) {
      modelId = asId(source.modelId) || asId(source.model_id);
    }
    if (!modelName) {
      modelName = String(source.modelName || source.model_name || "").trim();
    }
  });
  return { paramFileId, paramFileName: name, modelId, modelName };
}

function humanizeSnakeCase(text) {
  if (!text) {
    return text;
  }
  if (RUNNER_VALUE_LABELS[text]) {
    return RUNNER_VALUE_LABELS[text];
  }
  let raw = String(text).trim();
  if (raw.startsWith("analysis_")) {
    raw = raw.slice("analysis_".length);
  }
  if (!raw.includes("_")) {
    return raw === raw.toLowerCase() ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
  }
  return raw
    .split("_")
    .map((part) => {
      const lower = part.toLowerCase();
      if (HUMANIZE_ACRONYMS.has(lower)) {
        return lower.toUpperCase();
      }
      if (HUMANIZE_WORD_EXPANSIONS[lower]) {
        return HUMANIZE_WORD_EXPANSIONS[lower];
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ");
}

function formatScalar(value) {
  if (value == null) {
    return "—";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string" ? RUNNER_VALUE_LABELS[item] || item : String(item)
      )
      .join(", ");
  }
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, val]) => `${key}: ${val}`)
      .join("; ");
  }
  if (typeof value === "string") {
    if (RUNNER_VALUE_LABELS[value]) {
      return RUNNER_VALUE_LABELS[value];
    }
    if (value.includes("_") && value === value.toLowerCase()) {
      return humanizeSnakeCase(value);
    }
    return value;
  }
  return String(value);
}

function builderRows(builder) {
  const rows = [];
  if (!builder || typeof builder !== "object") {
    return rows;
  }
  const scale = builder.scale_params || {};
  ["temp_scale", "comp_block_scale"].forEach((key) => {
    if (key in scale) {
      rows.push({ label: BUILDER_LABELS[key], value: formatScalar(scale[key]) });
    }
  });
  const ofs = scale.other_feature_scales || {};
  if (ofs && typeof ofs === "object") {
    Object.entries(ofs).forEach(([featKey, val]) => {
      rows.push({
        label: OTHER_FEATURE_LABELS[featKey] || `${featKey} Scale`,
        value: formatScalar(val),
      });
    });
  }
  const pca = builder.pca_params || {};
  if ("n_components" in pca) {
    rows.push({ label: BUILDER_LABELS.n_components, value: formatScalar(pca.n_components) });
  }
  const umap = builder.umap_params || {};
  if ("n_components" in umap) {
    rows.push({ label: "UMAP Components", value: formatScalar(umap.n_components) });
  }
  if ("n_neighbors" in umap) {
    rows.push({ label: BUILDER_LABELS.n_neighbors, value: formatScalar(umap.n_neighbors) });
  }
  const lump = builder.lump_params || {};
  if ("custom_scheme" in lump) {
    rows.push({
      label: BUILDER_LABELS.custom_scheme,
      value: formatScalar(lump.custom_scheme),
    });
  }
  ["include_custom_distance_metric", "lumped_derivatives"].forEach((key) => {
    if (key in builder) {
      rows.push({ label: BUILDER_LABELS[key], value: formatScalar(builder[key]) });
    }
  });
  return rows;
}

function assemblerRows(assembler) {
  const rows = [];
  if (!assembler || typeof assembler !== "object") {
    return rows;
  }
  (assembler.qc_rules || []).forEach((rule) => {
    if (!rule || typeof rule !== "object") {
      return;
    }
    const name = rule.name || "rule";
    const params = rule.params || {};
    if (name === "remove_ids") {
      const ids = params.ids_to_remove || [];
      rows.push({
        label: "Sample IDs to Remove",
        value: ids.length ? formatScalar(ids) : "(None)",
      });
    } else if (name === "rs_gor_check") {
      rows.push({
        label: "GOR Check Tolerance Factor",
        value: formatScalar(params.tolerance_factor),
      });
    } else if (name !== "visc_patm_threshold") {
      rows.push({ label: String(name), value: formatScalar(params) });
    }
  });
  const cols = assembler.columns_to_remove_from_experiment_features || [];
  if (cols.length) {
    rows.push({ label: "Columns to Remove", value: formatScalar(cols) });
  }
  return rows;
}

function runnerSections(runner) {
  const sections = [];
  if (!runner || typeof runner !== "object") {
    return sections;
  }
  if (runner.analysis_runner_name) {
    sections.push({
      title: "",
      rows: [
        {
          label: RUNNER_LABELS.analysis_runner_name,
          value: formatScalar(runner.analysis_runner_name),
        },
      ],
    });
  }
  [
    ["fluid_analyser_params", "Fluid Analyser"],
    ["property_predictor_params", "Property Predictor"],
  ].forEach(([blockKey, blockLabel]) => {
    const block = runner[blockKey] || {};
    if (!block || typeof block !== "object" || !Object.keys(block).length) {
      return;
    }
    sections.push({
      title: blockLabel,
      rows: Object.entries(block).map(([key, value]) => ({
        label: RUNNER_LABELS[key] || humanizeSnakeCase(key),
        value: formatScalar(value),
      })),
    });
  });
  return sections;
}

function parseLumpBoundaries(scheme) {
  if (scheme == null) {
    return [];
  }
  if (typeof scheme === "string") {
    return scheme
      .split(",")
      .map((part) => parseInt(part.trim(), 10))
      .filter((num) => Number.isFinite(num));
  }
  if (Array.isArray(scheme)) {
    return scheme.map((item) => parseInt(item, 10)).filter((num) => Number.isFinite(num));
  }
  return [];
}

function lumpEntries(scheme) {
  const boundaries = parseLumpBoundaries(scheme);
  if (boundaries.length < 2) {
    return [];
  }
  const entries = [];
  for (let idx = 0; idx < boundaries.length - 1; idx += 1) {
    const startIdx = boundaries[idx];
    const endIdx = boundaries[idx + 1];
    const components = [];
    for (let j = startIdx; j < endIdx; j += 1) {
      if (COMPONENT_MAPPING[j]) {
        components.push(COMPONENT_MAPPING[j]);
      }
    }
    entries.push({ lumpNum: idx + 1, components });
  }
  return entries;
}

export function buildParamView(row, modelName = "") {
  const main =
    row?.mainConfig && typeof row.mainConfig === "object"
      ? row.mainConfig
      : row?.MainConfig && typeof row.MainConfig === "object"
        ? row.MainConfig
        : {};
  const runner =
    row?.runnerConfig && typeof row.runnerConfig === "object" ? row.runnerConfig : {};
  const builder =
    main.builder_config && typeof main.builder_config === "object"
      ? main.builder_config
      : {};
  const assembler =
    main.assembler_config && typeof main.assembler_config === "object"
      ? main.assembler_config
      : {};
  const displayName =
    modelName ||
    row?.modelName ||
    main.experiment_name ||
    paramFileName(row) ||
    "Model";

  return {
    title: `Expanded: ${displayName} - Parameter Detail`,
    builderRows: builderRows(builder),
    assemblerRows: assemblerRows(assembler),
    lumpEntries: lumpEntries(builder.lump_params?.custom_scheme),
    runnerSections: runnerSections(runner),
  };
}

async function getParamFile(apiGet, paramId) {
  const id = asId(paramId);
  if (!id) {
    return null;
  }
  try {
    const response = await apiGet(`public/api/pvt-params/${id}`);
    if (!isSuccessStatus(response)) {
      return null;
    }
    const row = unwrapEntity(unwrapPayload(response));
    return row && typeof row === "object" && Object.keys(row).length ? row : null;
  } catch (_) {
    return null;
  }
}

async function listParamFiles(apiGet, modelId, experimentType) {
  const params = { modelId };
  if (experimentType) {
    params.experimentType = experimentType;
  }
  try {
    const response = await apiGet("public/api/pvt-params", { params });
    if (!isSuccessStatus(response)) {
      return [];
    }
    return asList(unwrapPayload(response)).map(unwrapEntity);
  } catch (_) {
    return [];
  }
}

function findParamByName(rows, name) {
  const target = String(name || "").trim();
  if (!target) {
    return null;
  }
  return (
    rows.find((row) => paramFileName(row) === target) ||
    rows.find((row) => paramFileName(row).toLowerCase() === target.toLowerCase()) ||
    null
  );
}

async function resolveParamIdByName(apiGet, modelId, experimentType, name) {
  const target = String(name || "").trim();
  if (!target || !modelId) {
    return "";
  }
  const expType = normalizePvtExperimentType(experimentType);
  let matched = findParamByName(await listParamFiles(apiGet, modelId, expType), target);
  if (!matched) {
    matched = findParamByName(await listParamFiles(apiGet, modelId), target);
  }
  return entityId(matched);
}

async function loadTestsSample(apiGet, route, expType) {
  const sampleId = getPvtApiSampleId(route);
  if (!sampleId) {
    return null;
  }
  const testArrayKey =
    normalizePvtExperimentType(expType) === "dv"
      ? "dltests"
      : normalizePvtExperimentType(expType) === "viscosity"
        ? "viscositytests"
        : "ccetests";
  try {
    const response = await apiGet(getPvtTestListEndpoint(sampleId, testArrayKey));
    if (!isSuccessStatus(response)) {
      return null;
    }
    const payload = unwrapPayload(response);
    if (!payload || typeof payload !== "object") {
      return null;
    }
    const nested = payload.sampleMeta || payload.pvtSample || payload.PvtSample;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
      return { ...payload, ...nested };
    }
    return payload;
  } catch (_) {
    return null;
  }
}

async function fetchParamRow(apiGet, ref, expType) {
  let paramFileId = asId(ref.paramFileId);
  if (!paramFileId && ref.modelId && ref.paramFileName) {
    paramFileId = await resolveParamIdByName(
      apiGet,
      ref.modelId,
      expType,
      ref.paramFileName
    );
  }
  if (!paramFileId) {
    return null;
  }
  const row = await getParamFile(apiGet, paramFileId);
  if (!row) {
    return null;
  }
  return { row, modelName: ref.modelName || row.modelName || "" };
}

async function loadParamForSample(apiGet, sample, expType, route) {
  let current = sample && typeof sample === "object" ? sample : null;
  let ref = paramRefForExperiment(current || {}, expType);
  const loaded = await fetchParamRow(apiGet, ref, expType);
  if (loaded) {
    return loaded;
  }
  const fromTests = await loadTestsSample(apiGet, route, expType);
  if (fromTests) {
    current = { ...(current || {}), ...fromTests };
    ref = paramRefForExperiment(current, expType);
    const retry = await fetchParamRow(apiGet, ref, expType);
    if (retry) {
      return retry;
    }
  }
  return { row: null, modelName: ref.modelName };
}

function csvCell(value) {
  const text = value == null ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export function buildParamCsv(view) {
  const rows = [["Section", "Parameter", "Value"]];
  const push = (section, label, value) => {
    const cell = value == null || value === "" ? "—" : value;
    rows.push([section, label || "", cell]);
  };

  if (view?.builderRows?.length) {
    view.builderRows.forEach((row) => push("Builder Configuration", row.label, row.value));
  } else {
    push("Builder Configuration", "", "—");
  }

  if (view?.assemblerRows?.length) {
    view.assemblerRows.forEach((row) =>
      push("Assembler Configuration", row.label, row.value)
    );
  } else {
    push("Assembler Configuration", "", "—");
  }

  if (view?.lumpEntries?.length) {
    view.lumpEntries.forEach((entry) =>
      push(
        "Lump Parameters",
        `Lump ${entry.lumpNum}`,
        (entry.components || []).join(", ") || "—"
      )
    );
  } else {
    push("Lump Parameters", "", "—");
  }

  if (view?.runnerSections?.length) {
    view.runnerSections.forEach((section) => {
      const sectionName = section.title
        ? `Runner Configuration - ${section.title}`
        : "Runner Configuration";
      (section.rows || []).forEach((row) => push(sectionName, row.label, row.value));
    });
  } else {
    push("Runner Configuration", "", "—");
  }

  return rows.map((cols) => cols.map(csvCell).join(",")).join("\n");
}

export function downloadParamCsv(view, experimentType = "cce") {
  if (!view) {
    return;
  }
  const csvContent = buildParamCsv(view);
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const slug = String(experimentType || "cce").replace(/[^a-z0-9]+/gi, "_");
  const fileName = `pvt_parameters_${slug}.csv`;
  const link = document.createElement("a");
  if (link.download === undefined) {
    return;
  }
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function loadPvtParamConfig({
  apiGet,
  route,
  sampleMeta,
  experimentType,
}) {
  const expType = normalizePvtExperimentType(experimentType);
  const { row, modelName } = await loadParamForSample(
    apiGet,
    sampleMeta,
    expType,
    route
  );
  if (!row) {
    return {
      ok: false,
      message: "Could not load parameter detail for this sample.",
    };
  }
  return {
    ok: true,
    view: buildParamView(row, modelName),
  };
}
